import React, { FC } from 'react'

import { Box, Grid, Stack, Typography, useTheme } from '@mui/material'
import { isNull } from 'lodash'
import { CircularProgressbar } from 'react-circular-progressbar'
import { useTranslation } from 'react-i18next'

import { EmptyData } from '../EmptyData'
import { ReportWidgetsHeader } from '../ReportWidgetsHeader'
import { StyledWidgetWrapper } from '../StyledWidgetWrapper'

import { RiskTabWidgetProps } from './types'

export const RiskTabWidget: FC<RiskTabWidgetProps> = ({
  label,
  description,
  positiveValue,
  positiveLabel,
  negativeValue,
  negativeLabel,
}) => {
  const { t } = useTranslation()
  const { palette, spacing } = useTheme()

  const percentage = (positiveValue / (positiveValue + negativeValue)) * 100

  return (
    <StyledWidgetWrapper>
      <Box ml={spacing(-1)}>
        <ReportWidgetsHeader text={label} explanation={description} />
      </Box>
      {isNull(positiveValue) ? (
        <Box mt={spacing(4)}>
          <EmptyData
            title={t('notEnoughData', 'Not enough data')}
            subtitle={t('pleaseCompleteJournalingSection')}
          />
        </Box>
      ) : (
        <Grid container width="100%" height={spacing(31.25)} spacing={1} mt={1}>
          <Grid item xs={12} sm={6} container justifyContent="center" alignItems="center">
            <Box width={spacing(16)}>
              <CircularProgressbar
                value={percentage}
                styles={{
                  path: {
                    stroke: palette.green[400],
                    strokeLinecap: 'butt',
                    transition: 'stroke-dashoffset 0.5s ease 0s',
                    transformOrigin: 'center center',
                  },
                  trail: {
                    stroke: palette.red[400],
                    strokeLinecap: 'butt',
                    transformOrigin: 'center center',
                  },
                }}
                strokeWidth={13}
              />
            </Box>
          </Grid>
          <Grid item container xs={12} sm={6} alignItems={'center'} justifyContent={'center'}>
            <Stack direction={{ xs: 'row', sm: 'column' }} spacing={{ xs: 6, sm: 2 }}>
              <Box display="flex" flexDirection="column">
                <Box display="flex" gap={spacing(1.5)} alignItems="center">
                  <Box
                    bgcolor={palette.green[400]}
                    minWidth={spacing(1)}
                    height={spacing(1)}
                    borderRadius={spacing(0.5)}
                  />
                  <Typography fontWeight="bold" fontSize={12} color={palette.grey[700]}>
                    {positiveLabel}
                  </Typography>
                </Box>
                <Typography variant="h4">{`${Math.round(positiveValue)}%`}</Typography>
              </Box>

              <Box display="flex" flexDirection="column">
                <Box display="flex" gap={spacing(1.5)} alignItems="center">
                  <Box
                    bgcolor={palette.red[400]}
                    minWidth={spacing(1)}
                    height={spacing(1)}
                    borderRadius={spacing(0.5)}
                  />
                  <Typography fontWeight="bold" fontSize={12} color={palette.grey[700]}>
                    {negativeLabel}
                  </Typography>
                </Box>
                <Typography variant="h4">{`${Math.round(negativeValue)}%`}</Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      )}
    </StyledWidgetWrapper>
  )
}
